import { axiosInstance } from "@/pages/auth/auth.context";
import { AxiosResponse } from "axios";

export interface Position {
  id: string;
  tableName: string;
  billPrice: number;
  employeesRequired: number;
  /** How many guests this table seats. Null when no one has set it. */
  guestsAccommodated: number | null;
  employeePay: number;
  allowsMultipleAssignments: boolean;
  description?: string;
  allowsOverlappingEvents?: boolean;
  isTable: boolean;
  requestable?: boolean;
  isManager?: boolean;
  isFlatRate?: boolean;
}

export interface PositionFilter {
  requestable?: boolean;
  isTable?: boolean;
  excludeUserSignedOffPositions?: boolean;
  excludeUserRequestedPositions?: boolean;
  searchTerm?: string;
}

class PositionService {
  private handleError(error: any): never {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }

  /**
   * Get positions with optional filtering
   */
  async getPositions(filter?: PositionFilter): Promise<Position[]> {
    try {
      const params = new URLSearchParams();

      if (filter?.requestable !== undefined) {
        params.append("requestable", filter.requestable.toString());
      }
      if (filter?.isTable !== undefined) {
        params.append("isTable", filter.isTable.toString());
      }
      if (filter?.excludeUserSignedOffPositions !== undefined) {
        params.append(
          "excludeUserSignedOffPositions",
          filter.excludeUserSignedOffPositions.toString(),
        );
      }
      if (filter?.excludeUserRequestedPositions !== undefined) {
        params.append(
          "excludeUserRequestedPositions",
          filter.excludeUserRequestedPositions.toString(),
        );
      }
      if (filter?.searchTerm) {
        params.append("searchTerm", filter.searchTerm);
      }

      const url = params.toString()
        ? `position?${params.toString()}`
        : "position";
      const response: AxiosResponse<Position[]> = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get a single position by ID
   */
  async getPositionById(id: string): Promise<Position | null> {
    try {
      const response: AxiosResponse<Position> = await axiosInstance.get(
        `position/${id}`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create a new position
   */
  async createPosition(position: Omit<Position, "id">): Promise<Position> {
    try {
      const response: AxiosResponse<Position> = await axiosInstance.post(
        "position",
        position,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing position
   */
  async updatePosition(
    id: string,
    position: Partial<Position>,
  ): Promise<Position> {
    try {
      const response: AxiosResponse<Position> = await axiosInstance.put(
        `position/${id}`,
        position,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const positionService = new PositionService();
